// MCP relay access — the account routes behind the "Koneksi AI" page
// (docs/API.md §12.4, DECISIONS D41/D45). The dashboard manages ACCESS only:
// who may use the relay for this account. It never runs a tool call and never
// sees a call's content (D42).
//
// While the server's relay switch is off (MCP_OAUTH_ENABLED=false) not one
// /api/v1/mcp route is mounted, so every call answers 404 `not_found`
// (D41 (9)). That is a state, not a failure: loadMcpAccess reports it as
// `available: false`, and the page says so calmly instead of showing an error.

import { ApiError, apiDelete, apiGet, apiPost } from "@/lib/api";
import { toError } from "@/lib/errors";
import type { McpCreatedKey, McpGrant, McpKey, McpRelayDevice } from "@/lib/types";

/** The relay's canonical resource (`MCP_RESOURCE`, docs/API.md §12): where a
 *  client sends a static key as `Authorization: Bearer <key>`. */
export const MCP_RELAY_URL = "https://mcp.ahcut.com/mcp";

/** The server's bound for a key name, after trimming (POST /mcp/keys). */
export const MCP_KEY_NAME_MAX = 60;

/** Expiry choices for a new key, in days. `null` sends no `expires_in_days`,
 *  which is the API's own default: a key that never expires. */
export const MCP_KEY_EXPIRY_OPTIONS: readonly { days: number | null; label: string }[] = [
  { days: null, label: "Tidak kedaluwarsa" },
  { days: 30, label: "30 hari" },
  { days: 90, label: "90 hari" },
  { days: 365, label: "1 tahun" },
];

// ---- Reads ----

export function listMcpGrants(signal?: AbortSignal): Promise<McpGrant[]> {
  return apiGet<McpGrant[]>("/mcp/grants", { signal }).then((v) => v ?? []);
}

export function listMcpKeys(signal?: AbortSignal): Promise<McpKey[]> {
  return apiGet<McpKey[]>("/mcp/keys", { signal }).then((v) => v ?? []);
}

export function listMcpRelayDevices(signal?: AbortSignal): Promise<McpRelayDevice[]> {
  return apiGet<McpRelayDevice[]>("/mcp/relay/devices", { signal }).then((v) => v ?? []);
}

/** A list that loaded, or the error it failed with — one per list, so a
 *  failure in one never blanks the other two. */
export type Settled<T> = { ok: true; value: T } | { ok: false; error: Error };

export type McpAccess =
  | { available: false }
  | {
      available: true;
      grants: Settled<McpGrant[]>;
      keys: Settled<McpKey[]>;
      /** null: this server predates GET /mcp/relay/devices (it answers 404 while
       *  the other two lists answer). */
      devices: Settled<McpRelayDevice[]> | null;
    };

function settle<T>(promise: Promise<T>): Promise<Settled<T>> {
  return promise.then(
    (value): Settled<T> => ({ ok: true, value }),
    (err: unknown): Settled<T> => ({ ok: false, error: toError(err) }),
  );
}

/** The route is not mounted on this server (the whole surface while the relay
 *  is off, or one route an older API does not have yet). */
function isMissing(result: Settled<unknown>): boolean {
  return !result.ok && result.error instanceof ApiError && result.error.status === 404;
}

/** Everything the page shows, in one parallel read. Never rejects: each list
 *  carries its own outcome, and a 404 on both grants and keys means the relay
 *  is switched off on this server. */
export async function loadMcpAccess(signal?: AbortSignal): Promise<McpAccess> {
  const [grants, keys, devices] = await Promise.all([
    settle(listMcpGrants(signal)),
    settle(listMcpKeys(signal)),
    settle(listMcpRelayDevices(signal)),
  ]);
  if (isMissing(grants) && isMissing(keys)) return { available: false };
  return { available: true, grants, keys, devices: isMissing(devices) ? null : devices };
}

// ---- Writes ----

/** A DELETE on a grant or a key answers 404 for "already revoked" (and for "not
 *  yours", which this page cannot produce: every id comes from the account's own
 *  list). Either way the state the person asked for already holds. */
async function revokeOrGone(request: Promise<unknown>): Promise<void> {
  try {
    await request;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return;
    throw err;
  }
}

/** Revokes one connected client and clears its consent, so it has to ask again. */
export function revokeMcpGrant(id: string): Promise<void> {
  return revokeOrGone(apiDelete<unknown>(`/mcp/grants/${encodeURIComponent(id)}`));
}

export function revokeMcpKey(id: string): Promise<void> {
  return revokeOrGone(apiDelete<unknown>(`/mcp/keys/${encodeURIComponent(id)}`));
}

/** Bumps the computer's epoch at the relay. Idempotent server-side. Not a ban:
 *  one connected at that moment gets 4010 and switches its own relay off until
 *  it is switched on again there; one not connected at that moment asks for a
 *  new ticket as soon as it can, which clears `revoked_at` (D45 (5)(c)). */
export async function revokeMcpRelayDevice(deviceId: string): Promise<void> {
  await apiDelete<unknown>(`/mcp/relay/devices/${encodeURIComponent(deviceId)}`);
}

/** Every grant, key, computer and consent at once. Login sessions are untouched. */
export async function revokeAllMcpAccess(): Promise<void> {
  await apiPost<unknown>("/mcp/revoke-all");
}

/** The response is the only place the plaintext key ever appears. */
export function createMcpKey(name: string, expiresInDays: number | null): Promise<McpCreatedKey> {
  return apiPost<McpCreatedKey>(
    "/mcp/keys",
    expiresInDays === null ? { name } : { name, expires_in_days: expiresInDays },
  );
}

/** The two refusals a person can act on, in their words; anything else keeps the
 *  server's message. The code stays, so ErrorState still shows it. */
export function keyCreateError(err: unknown): Error {
  if (err instanceof ApiError && err.code === "conflict") {
    return new ApiError(
      err.code,
      "Batas kunci tercapai: terlalu banyak kunci yang masih aktif, atau terlalu banyak kunci dibuat dalam sehari terakhir. Cabut kunci yang tidak dipakai lagi (kunci yang kedaluwarsa tetap dihitung sampai dicabut), atau coba lagi nanti.",
      err.status,
    );
  }
  if (err instanceof ApiError && err.code === "validation") {
    return new ApiError(
      err.code,
      `Nama kunci harus diisi, paling banyak ${MCP_KEY_NAME_MAX} karakter.`,
      err.status,
    );
  }
  return toError(err);
}

// ---- Display ----

/** A client id as the consent page shows it: an https URL id (a client metadata
 *  document) shortened to its host, anything else verbatim
 *  (api/internal/mcpauth/consent.go displayClientID). */
export function mcpClientIdLabel(clientId: string): string {
  if (clientId.startsWith("https://")) {
    try {
      return new URL(clientId).host;
    } catch {
      return clientId;
    }
  }
  return clientId;
}

/** A client's name as the consent page shows it: the declared name, or its id
 *  when it declared none (consent.go displayName). */
export function mcpClientLabel(grant: Pick<McpGrant, "client_name" | "client_id">): string {
  return grant.client_name.trim() || mcpClientIdLabel(grant.client_id);
}

/** The desktop's masking of a key prefix (desktop src/lib/mcpConnect.ts
 *  maskToken): at most six mask characters, then the last four readable, so one
 *  key reads the same in the app and here. The prefix is a public lookup handle,
 *  not a secret; the mask character is data, not an icon. */
export function maskKeyPrefix(prefix: string): { mask: string; tail: string } {
  const hidden = Math.min(6, Math.max(0, prefix.length - 4));
  return { mask: "•".repeat(hidden), tail: prefix.slice(-4) };
}

/** A relay device id is 22–64 URL-safe characters; the list shows its start. */
export function shortDeviceId(deviceId: string): string {
  return deviceId.length > 8 ? `${deviceId.slice(0, 8)}…` : deviceId;
}

/** Past its `expires_at`: refused by the relay, but listed until revoked. */
export function isMcpKeyExpired(key: Pick<McpKey, "expires_at">, now: number = Date.now()): boolean {
  if (!key.expires_at) return false;
  const at = new Date(key.expires_at).getTime();
  return !Number.isNaN(at) && at <= now;
}
